"use client";

import { TooltipContent } from "@/components/ui/tooltip-content";
import { SITE_OWNER_NAME, SITE_OWNER_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { InfoIcon } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";
import CommunityCtaBox from "../blog/community-cta";
import { RetroGrid } from "../ui/retro-grid";

const companyLinks = [
	{
		label: "Contact",
		href: "/contact",
	},
	{
		label: "Careers",
		href: "/careers",
	},
	{
		label: "About Us",
		href: "/about",
	},
];

const documentLinks = [
	{
		label: "Terms",
		href: "/terms",
		tooltip: "Read our terms and conditions",
	},
	{
		label: "Security",
		href: "/security",
		tooltip: "Read our security policy",
	},
	{
		label: "Cookies",
		href: "/cookies",
		tooltip: "Read our cookies policy",
	},
	{
		label: "Privacy",
		icon: "/images/privacy-options.svg",
		href: "/privacy",
		tooltip: "Read our privacy policy",
	},
];

const resourcesLinks = [
	{
		label: "Help Center",
		href: "/help",
	},
	{
		label: "Changelog",
		href: "/changelog",
	},
	{
		label: "Status",
		href: "/status",
	},
	{
		label: "Blog",
		href: "/blog",
	},
];

const useCaseLinks = [
	{
		label: "Events Map",
		href: "/products/use-case/events-map",
	},
	{
		label: "City Map",
		href: "/products/use-case/city-map",
	},
	{
		label: "Travel Map",
		href: "/products/use-case/travel-map",
	},
	{
		label: "Community Map",
		href: "/products/use-case/community-map",
	},
	// {
	// 	label: "All Use Cases",
	// 	href: "/products/use-cases",
	// },
];

const productLinks = [
	{
		label: "Live Maps",
		href: "/features",
	},
	// {
	// 	label: "Pricing",
	// 	href: "/pricing",
	// },
	{
		label: "Community",
		href: "/community",
	},
	{
		label: "Blog",
		href: "/blog",
	},
	// {
	// 	label: "Enterprise",
	// 	href: "/enterprise",
	// },
	// {
	// 	label: "Map Templates",
	// 	href: "/product/map-templates",
	// },
];

const downloadLinks = [
	{
		label: "Web",
		href: "/web",
		active: true,
	},
	// {
	// 	label: "Mobile",
	// 	href: "/mobile",
	// 	active: false,
	// },
	{
		label: "iOS",
		href: "/mobile/ios",
		active: false,
	},
	{
		label: "Mac",
		href: "/desktop/mac",
		active: false,
	},
	{
		label: "Android",
		href: "/mobile/android",
		active: false,
	},
	// {
	// 	label: "Desktop",
	// 	href: "/desktop",
	// 	active: false,
	// },
	{
		label: "Windows",
		href: "/desktop/windows",
		active: false,
	},
];

interface FooterProps {
	showTicker?: boolean;
}

export const Footer = ({ showTicker = true }: FooterProps) => {
	return (
		<footer
			className={cn(
				"w-full relative mt-auto z-0 backdrop-blur-sm bg-primary/5",
				"border-t border-primary/20",
			)}
		>
			{/* Footer */}
			<RetroGrid />

			<AnimatedContainer>
				<div className="p-8 mx-auto max-w-6xl">
					<div
						className={cn(
							"w-full flex items-center justify-center flex-col lg:flex-row lg:gap-8 mx-auto",
						)}
					>
						{/* Community CTA */}
						<CommunityCtaBox className="flex-auto" variant="slim" />

						{/* Links */}
						<div className="grid grid-cols-2 md:grid-cols-5 gap-12 p-6 lg:p-2 justify-between">
							{/* Product */}
							<div className="flex flex-col gap-1">
								<h3 className="text-primary text-md font-semibold">Platform</h3>
								<nav className="flex flex-col gap-0.25">
									{productLinks.map((option, index) => (
										<AnimatedContainer
											key={option.label}
											delay={0.1 + index * 0.1}
										>
											<Link
												href={option.href}
												key={option.label}
												className="text-xs text-nowrap text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
											>
												{option.label}
											</Link>
										</AnimatedContainer>
									))}
								</nav>
							</div>

							{/* Maps */}
							<div className="flex flex-col gap-1">
								<h3 className="text-primary text-md font-semibold">Maps</h3>
								<nav className="flex flex-col gap-0.25">
									{useCaseLinks.map((option, index) => (
										<AnimatedContainer
											key={option.label}
											delay={0.1 + index * 0.1}
										>
											<Link
												href={option.href}
												key={option.label}
												className="text-xs text-nowrap text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
											>
												{option.label}
											</Link>
										</AnimatedContainer>
									))}
								</nav>
							</div>

							{/* Platforms */}
							<div className="flex flex-col gap-1">
								<h3 className="text-primary text-md font-semibold">
									Platforms
								</h3>
								<nav className="flex flex-col gap-0.25">
									{downloadLinks.map((option, index) => (
										<AnimatedContainer
											key={option.label}
											delay={0.1 + index * 0.1}
										>
											{option.active ? (
												<Link
													href={option.href}
													key={option.label}
													className="text-xs text-nowrap text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
												>
													{option.label}
												</Link>
											) : (
												<span className="items-center justify-center text-xs text-nowrap text-primary/40">
													{option.label}
													<div className="inline-flex items-center justify-center">
														<TooltipContent content="Coming soon">
															<InfoIcon className="inline ml-1 h-3 w-3 text-primary/30" />
														</TooltipContent>
													</div>
												</span>
											)}
										</AnimatedContainer>
									))}
								</nav>
							</div>

							{/* Company */}
							<div className="flex flex-col gap-1">
								<h3 className="text-primary text-md font-semibold">Company</h3>
								<nav className="flex flex-col gap-0.25">
									{companyLinks.map((option, index) => (
										<AnimatedContainer
											key={option.label}
											delay={0.1 + index * 0.1}
										>
											<Link
												href={option.href}
												key={option.label}
												className="text-xs text-nowrap text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
											>
												{option.label}
											</Link>
										</AnimatedContainer>
									))}
								</nav>
							</div>

							{/* Resources */}
							<div className="flex flex-col gap-1">
								<h3 className="text-primary text-md font-semibold">
									Resources
								</h3>
								<nav className="flex flex-col gap-0.25">
									{resourcesLinks.map((option, index) => (
										<AnimatedContainer
											key={option.label}
											delay={0.1 + index * 0.1}
										>
											<Link
												href={option.href}
												key={option.label}
												className="text-xs text-nowrap text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
											>
												{option.label}
											</Link>
										</AnimatedContainer>
									))}
								</nav>
							</div>
						</div>
					</div>

					{/* Last line */}
					<div className="flex flex-col mt-8 md:flex-row mx-auto p-2 items-center md:items-start gap-y-2 justify-between">
						<div className="flex flex-col md:flex-row items-center space-x-4 gap-2">
							<DocumentLinks />
						</div>
						<div className="flex items-center gap-4">
							<span className="text-xs text-center text-primary/80">
								&copy; {new Date().getFullYear()}{" "}
								<Link
									href={SITE_OWNER_URL}
									className="text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer"
								>
									{SITE_OWNER_NAME}
								</Link>
								. All rights reserved.
							</span>
						</div>
					</div>
				</div>
			</AnimatedContainer>
		</footer>
	);
};

function DocumentLinks() {
	return (
		<div className="flex flex-col gap-2">
			{/* <h3 className="text-primary/30 text-lg font-semibold">Documents</h3> */}
			<div className="flex flex-row gap-3">
				{documentLinks.map((option) => (
					<TooltipContent content={option.tooltip} key={option.label}>
						<Link
							href={option.href}
							key={option.label}
							className={cn(
								"flex flex-row gap-1 items-center justify-center",
								"text-xs text-primary/80 transition-colors hover:text-primary hover:underline hover:underline-offset-4 cursor-pointer",
							)}
						>
							{option.label}

							{/* Icon */}
							{option.icon && (
								<Image
									src={option.icon}
									alt={option.label}
									className="h-auto w-6"
									height={8}
									width={8}
								/>
							)}
						</Link>
					</TooltipContent>
				))}
			</div>
		</div>
	);
}

type ViewAnimationProps = {
	delay?: number;
	className?: ComponentProps<typeof motion.div>["className"];
	children: ReactNode;
};

function AnimatedContainer({
	className,
	delay = 0.1,
	children,
}: ViewAnimationProps) {
	const shouldReduceMotion = useReducedMotion();

	if (shouldReduceMotion) {
		return children;
	}

	return (
		<motion.div
			initial={{ filter: "blur(4px)", translateY: -8, opacity: 0 }}
			whileInView={{ filter: "blur(0px)", translateY: 0, opacity: 1 }}
			viewport={{ once: true }}
			transition={{ delay, duration: 0.8 }}
			className={className}
		>
			{children}
		</motion.div>
	);
}
